import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {Observable, combineLatest, of} from 'rxjs';
import {AuthService, OrgService} from '../../core';
import {select, Store} from '@ngrx/store';
import * as fromRoot from '../../reducers';
import * as fromAfbApi from '../../afb-api/reducers';
import * as fromAfbGraphqlApi from '../../afb-graphql-api/reducers';
import {OrganizationWithToken} from '../../core/models/core';
import {SelectOrganizationSuccess} from '../../core/actions/core.actions';
import {GetOrganizationDevices} from '../../afb-api/actions/device.actions';
import * as group from '../../core/store/actions/group.action';
import {filter, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {GetSelfOrganizationUser} from '../../afb-api/actions/organization-user.actions';
import {GetOrganizationFloors} from '../../afb-api/actions/floor-plan.actions';
import {mergeMap} from 'rxjs/internal/operators';
import {GetSelfOrganization} from '../../afb-graphql-api/actions/organization.actions';
import {LoginComplete, Logout} from '../../afb-graphql-api/actions/auth.actions';
import {User} from '../../afb-graphql-api/models/auth';
import {Organization} from '../../afb-graphql-api/models/organization';

@Injectable()
export class DashboardGuard implements CanActivate {
  accessToken$: Observable<string> = this.afbApiStore.pipe(select(fromAfbGraphqlApi.accessToken));
  user$: Observable<User> = this.afbGraphqlApiStore.pipe(select(fromAfbGraphqlApi.user));
  selectedOrganization$: Observable<OrganizationWithToken> = this.rootStore.pipe(select(fromRoot.selectedOrganization));

  constructor(private rootStore: Store<fromRoot.State>,
              private afbApiStore: Store<fromAfbApi.State>,
              private afbGraphqlApiStore: Store<fromAfbGraphqlApi.State>,
              private authService: AuthService,
              private orgService: OrgService,
              private router: Router) {
  }

  waitForSelfOrganizationToLoad(organizationToken: string): Observable<boolean> {
    return this.afbApiStore.pipe(
      select(fromAfbGraphqlApi.isGetSelfOrganizationLoaded),
      tap(() => this.afbApiStore.dispatch(new GetSelfOrganization({organizationToken}))),
      filter((isSelfOrganizationLoaded: boolean) => isSelfOrganizationLoaded),
      withLatestFrom(this.afbApiStore.pipe(select(fromAfbGraphqlApi.getSelfOrganizationError))),
      map(([isSelfOrganizationLoaded, errorMessage]: [boolean, string]) => errorMessage == null),
      take(1)
    );
  }

  initMasterDashboard(organizationToken: string): Observable<boolean> {
    return this.afbApiStore.pipe(
      select(fromAfbGraphqlApi.selfOrganization),
      map((selfOrganization: Organization) => {
        const organizationWithToken: OrganizationWithToken = {
          ...selfOrganization,
          token: organizationToken
        };
        this.afbApiStore.dispatch(new GetSelfOrganizationUser({organizationToken: organizationWithToken.token}));
        this.rootStore.dispatch(new SelectOrganizationSuccess(organizationWithToken));
        this.rootStore.dispatch(new GetOrganizationDevices({organizationToken: organizationWithToken.token}));
        // this.rootStore.dispatch(new user.RequestSelfOrgInfoAction(organizationWithToken.token));
        this.rootStore.dispatch(new group.RequestGroupListAction(organizationWithToken.token));
        this.afbApiStore.dispatch(new GetOrganizationFloors({organizationToken: organizationWithToken.token}));

        return true;
      })
    );
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return combineLatest(this.accessToken$, this.user$, this.selectedOrganization$,
      (accessToken: string, user: User, selectedOrganization: OrganizationWithToken) => {
        return {accessToken, user, selectedOrganization};
      }).pipe(
      mergeMap(({accessToken, user, selectedOrganization}) => {
        const token = next.queryParams['internalToken'] || accessToken;
        if (token != null) {
          const accessTokenFromParams: string = next.queryParams['internalToken'];
          const orgTokenFromParams: string = next.queryParams['dashboardToken'];

          if (accessTokenFromParams != null && orgTokenFromParams != null) {
            // Master Dashboard
            this.afbGraphqlApiStore.dispatch(new LoginComplete({
              accessToken: accessTokenFromParams,
              id: user.id.toString(),
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              dobYear: user.dobYear,
              dobMonth: user.dobMonth,
              dobDay: user.dobDay,
              gender: user.gender
            }));

            return this.waitForSelfOrganizationToLoad(orgTokenFromParams).pipe(
              switchMap((isSuccessfullyLoaded: boolean) => isSuccessfullyLoaded ? this.initMasterDashboard(orgTokenFromParams) :
                of(false))
            );
          } else if (selectedOrganization != null) {
            // Not Master Dashboard
            this.rootStore.dispatch(new GetOrganizationDevices({organizationToken: selectedOrganization.token}));
            this.afbApiStore.dispatch(new GetSelfOrganizationUser({organizationToken: selectedOrganization.token}));
            this.rootStore.dispatch(new group.RequestGroupListAction(selectedOrganization.token));
            this.afbApiStore.dispatch(new GetOrganizationFloors({organizationToken: selectedOrganization.token}));

            return of(true);
          } else {
            // Did not select organization yet
            this.router.navigate(['/org/list']);

            return of(false);
          }
        } else {
          // Not logged in
          this.afbApiStore.dispatch(new Logout());

          return of(false);
        }
      })
    );
  }
}
